class FacetsNavigationComponent extends HTMLElement {
  static SELECTORS = {
    navItem: '.facets-navigation__item',
    navLink: '.facets-navigation__link--filter',
    facetFilters: 'facet-filters',
    facetForm: '#facet-filters-form',
  };

  constructor() {
    super();
    this.navItems = this.querySelectorAll(FacetsNavigationComponent.SELECTORS.navItem);
  }

  connectedCallback() {
    this.addEventListener('click', this.#handleNavigationClick);
    // Listen to facet changes at document level
    document.addEventListener('facet:criteria-changed', this.#handleFilterUpdate);
    // Initialize filters from URL parameters on page load
    this.#initializeFiltersFromURL();
    this.#updateActiveStates();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleNavigationClick);
    document.removeEventListener('facet:criteria-changed', this.#handleFilterUpdate);
  }

  /**
   * @param {MouseEvent} event
   */
  #handleNavigationClick = (event) => {
    const navigationItem = event.target.closest(FacetsNavigationComponent.SELECTORS.navItem);
    if (!navigationItem) return;
    const { filterParam, filterValue } = navigationItem?.dataset || {};

    if (!filterParam || !filterValue) {
      return;
    }
    event.preventDefault();
    this.#syncWithFilterCheckboxes(filterParam, filterValue);
  };

  /**
   * @param {string} param
   * @param {string} value
   */
  #syncWithFilterCheckboxes = (param, value) => {
    // Find or create the checkbox for the clicked filter first
    let checkbox = this.#findFilterCheckbox(param, value);
    
    // If checkbox doesn't exist, create it dynamically
    if (!checkbox) {
      checkbox = this.#createFilterCheckbox(param, value);
    }
    
    if (!checkbox) {
      return;
    }

    // Save the current state before unchecking all
    const wasChecked = checkbox.checked;

    // Find all checkboxes with the same parameter and uncheck them
    const allCheckboxesWithSameParam = document.querySelectorAll(`input[name="${param}"]`);
    allCheckboxesWithSameParam.forEach(cb => {
      if (cb.checked) {
        cb.checked = false;
        const changeEvent = new Event('change', { bubbles: true });
        cb.dispatchEvent(changeEvent);
      }
    });

    // If the clicked filter was not checked, check it now (toggle on)
    // If it was checked, it stays unchecked (toggle off)
    if (!wasChecked) {
      checkbox.checked = true;
      const changeEvent = new Event('change', { bubbles: true });
      checkbox.dispatchEvent(changeEvent);
    }
    
    // Also trigger the facet:criteria-changed event directly since we're outside the facet-filters scope
    this.#triggerFacetCriteriaChanged();
  };

  #updateActiveStates = () => {
    this.navItems.forEach(item => {
      const { filterParam, filterValue } = item.dataset;
      if (!filterParam || !filterValue) return;

      const checkbox = this.#findFilterCheckbox(filterParam, filterValue);
      const isActive = checkbox?.checked || false;
      
      item.classList.toggle('facets-navigation__item--active', isActive);
    });
  };

  /**
   * Convert a string to handleized format for comparison
   * @param {string} value
   * @returns {string}
   */
  #handleize = (value) => {
    return value.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  /**
   * @param {string} param
   * @param {string} value
   * @returns {HTMLInputElement | null}
   */
  #findFilterCheckbox = (param, value) => {
    // Theme uses input[name="filter.x.y"][value="encoded-value"]
    const selector = `input[name="${param}"][value="${value}"]`;
    let checkbox = document.querySelector(selector);
    
    // If not found, try with URL decoded value
    if (!checkbox) {
      const decodedValue = decodeURIComponent(value);
      const decodedSelector = `input[name="${param}"][value="${decodedValue}"]`;
      checkbox = document.querySelector(decodedSelector);
    }
    
    // If still not found, try with handleized value
    if (!checkbox) {
      const handleizedValue = this.#handleize(value);
      const handleizedSelector = `input[name="${param}"][value="${handleizedValue}"]`;
      checkbox = document.querySelector(handleizedSelector);
    }
    
    return checkbox instanceof HTMLInputElement ? checkbox : null;
  };

  /**
   * @param {string} param
   * @param {string} value
   * @returns {HTMLInputElement | null}
   */
  #createFilterCheckbox = (param, value) => {
    const form = document.querySelector(FacetsNavigationComponent.SELECTORS.facetForm);
    if (!form) {
      return null;
    }

    // Decode the value since it's already URL-encoded from the custom filter
    const decodedValue = decodeURIComponent(value);

    // Create checkbox element
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.name = param;
    checkbox.value = decodedValue;
    checkbox.className = 'visually-hidden';
    checkbox.id = `${param}-${decodedValue}`.replace(/[^a-zA-Z0-9-_]/g, '-');

    // Add to form
    form.appendChild(checkbox);
    
    return checkbox;
  };

  /**
   * Trigger the facet:criteria-changed event to update the product list
   */
  #triggerFacetCriteriaChanged = () => {
    const form = document.querySelector(FacetsNavigationComponent.SELECTORS.facetForm);
    if (!form) {
      return;
    }

    const formData = new FormData(form);
    const searchParams = new URLSearchParams();
    
    // Manually build search params to ensure proper encoding
    for (const [key, value] of formData.entries()) {
      if (value) {
        searchParams.append(key, value);
      }
    }
    
    const searchParamsAsString = searchParams.toString();
    const url = `${window.location.pathname}?${searchParamsAsString}`;
    
    // Dispatch the event on the product-facet component
    const productFacet = document.querySelector('product-facet');
    if (productFacet) {
      productFacet.dispatchEvent(new CustomEvent('facet:criteria-changed', {
        bubbles: true,
        detail: { url }
      }));
    } else {
      // Fallback: dispatch on document
      document.dispatchEvent(new CustomEvent('facet:criteria-changed', {
        bubbles: true,
        detail: { url }
      }));
    }
  };

  #handleFilterUpdate = () => {
    this.#updateActiveStates();
  };

  /**
   * Check if two values match (exact or handleized)
   * @param {string} filterValue
   * @param {string} urlValue
   * @returns {boolean}
   */
  #valuesMatch = (filterValue, urlValue) => {
    const decodedFilter = decodeURIComponent(filterValue);
    const decodedUrl = decodeURIComponent(urlValue);
    
    // Exact match
    if (decodedFilter === decodedUrl) {
      return true;
    }
    
    // Handleized match
    return this.#handleize(decodedFilter) === this.#handleize(decodedUrl);
  };

  /**
   * Initialize filters from URLSearchParams on page load
   */
  #initializeFiltersFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    
    this.navItems.forEach(item => {
      const { filterParam, filterValue } = item.dataset;
      if (!filterParam || !filterValue) return;

      // Check if this filter parameter exists in URL
      let shouldActivate = false;
      const urlValue = urlParams.get(filterParam);
      
      if (urlValue && this.#valuesMatch(filterValue, urlValue)) {
        shouldActivate = true;
      } else {
        // Check all values for this parameter (multi-value filters)
        const allUrlValues = urlParams.getAll(filterParam);
        for (const value of allUrlValues) {
          if (this.#valuesMatch(filterValue, value)) {
            shouldActivate = true;
            break;
          }
        }
      }

      if (shouldActivate) {
        // Find or create checkbox and set it to checked
        let checkbox = this.#findFilterCheckbox(filterParam, filterValue);
        
        if (!checkbox) {
          checkbox = this.#createFilterCheckbox(filterParam, filterValue);
        }

        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
          // Trigger change event to ensure form state is updated
          const changeEvent = new Event('change', { bubbles: true });
          checkbox.dispatchEvent(changeEvent);
        }
      }
    });
  };
}

if (!customElements.get('facets-navigation-component')) {
  customElements.define('facets-navigation-component', FacetsNavigationComponent);
}